import { DEFAULT_ACCOUNT_APPEARANCE, type AccountAppearance } from './auth';

/** V2 主题只描述平台可渲染的视觉元素；高级样式也只能作用于受控主题槽位。 */
export const THEME_ASSET_ROLES = [
  'background',
  'texture',
  'illustration',
  'icon-set',
  'font',
  'animation',
  'video',
  'poster',
] as const;
export type ThemeAssetRole = (typeof THEME_ASSET_ROLES)[number];

export type ThemeAssetContentType =
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'image/svg+xml'
  | 'image/apng'
  | 'application/json'
  | 'video/mp4'
  | 'video/webm'
  | 'font/woff2';

export interface ThemeAssetReference {
  id: string;
  role: ThemeAssetRole;
  /** 仅后端使用的不可变对象键；前端只能消费 runtime.assetUrls。 */
  objectKey: string;
  contentType: ThemeAssetContentType;
  sha256: string;
  width?: number;
  height?: number;
  durationMs?: number;
}

export interface ThemePalette {
  primary: string;
  canvas: string;
  surface: string;
  elevated: string;
  text: string;
  textMuted: string;
  border: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
}

export interface ThemeTypography {
  fontFamily: AccountAppearance['fontFamily'];
  fontSize: AccountAppearance['fontSize'];
  lineHeight: AccountAppearance['lineHeight'];
}

export const THEME_SURFACES = [
  'shell',
  'navigation',
  'header',
  'panel',
  'card',
  'overlay',
  'control',
  'component',
] as const;
export type ThemeSurface = (typeof THEME_SURFACES)[number];

/** 平台将这些参数编译成 CSS；主题作者无法插入选择器或任意属性。 */
export interface ThemeSurfaceRecipe {
  tint: string;
  opacity: number;
  blurPx: number;
  saturation: number;
  borderColor: string;
  borderOpacity: number;
  /** 内高光也必须由主题声明，不能在组件 CSS 中固定为白色。 */
  highlightColor: string;
  highlightOpacity: number;
  /** 阴影色随主题材质变化，运行时仅决定阴影的几何参数。 */
  shadowColor: string;
  shadow: 'none' | 'soft' | 'float';
  radius: number;
}

export interface ThemeSceneLayer {
  id: string;
  type: 'color' | 'gradient' | 'image' | 'texture' | 'video';
  color?: string;
  colors?: [string, string, string?];
  assetId?: string;
  opacity: number;
  position?: 'cover' | 'contain' | 'center';
  motion?: 'none' | 'drift' | 'float';
}

export interface ThemeDecoration {
  id: string;
  assetId: string;
  anchor: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  width: 'small' | 'medium' | 'large';
  opacity: number;
  motion: 'none' | 'float' | 'drift' | 'pulse';
  hideOnMobile?: boolean;
}

/** 主题样式包可命中的平台槽位，避免样式越界影响业务布局或安全交互。 */
export const THEME_STYLE_SLOTS = [
  '.ws-theme-root',
  '.ws-theme-root[data-theme-mode="dark"]',
  '.ws-theme-stage',
  '.ws-theme-stage::before',
  '.ws-theme-root [data-theme-surface="shell"]',
  '.ws-theme-root [data-theme-surface="navigation"]',
  '.ws-theme-root [data-theme-surface="header"]',
  '.ws-theme-root [data-theme-surface="panel"]',
  '.ws-theme-root .ant-card',
  '.ws-theme-root .ant-btn',
  '.ws-theme-root .ant-btn-primary',
  '.ws-theme-root .ant-input',
  '.ws-theme-root .ant-input-affix-wrapper',
  '.ws-theme-root .ant-select-selector',
  '.ws-theme-root .agent-composer-shell',
  '.ws-theme-root .message-bubble--user',
  '.ws-theme-root .message.user .message-content',
  '.ws-theme-root .message-row.user .message-content',
] as const;

export interface ThemeStyleSheet {
  version: 1;
  /** 已校验的 CSS 源码；素材仅可通过 url("asset://素材ID") 引用。 */
  source: string;
}

export interface ThemeVariant {
  palette: ThemePalette;
  scene: { layers: ThemeSceneLayer[] };
  surfaces: Record<ThemeSurface, ThemeSurfaceRecipe>;
  decorations: ThemeDecoration[];
}

/** 商店中不可变的主题产物。V2 没有 glass、anime 等固定风格枚举。 */
export interface ThemeManifestV2 {
  type: 'theme';
  schemaVersion: 2;
  name: string;
  description: string;
  typography: ThemeTypography;
  density: AccountAppearance['density'];
  assets: ThemeAssetReference[];
  styleSheet?: ThemeStyleSheet | null;
  light: ThemeVariant;
  dark?: ThemeVariant;
  allowPublicDerivatives: boolean;
}

export interface ThemeReleaseReference {
  packageId: string;
  releaseId: string;
}

/** 与主题选择分离，用户仍能在任意主题下保留可访问性偏好。 */
export interface UserVisualPreferences {
  mode: 'light' | 'dark';
  contrast: AccountAppearance['contrast'];
  transparency: 'full' | 'reduced';
  /** 这些是账户级安全覆盖项，优先于主题清单的同类展示参数。 */
  primaryColor: string | null;
  baseRadius: number | null;
  density: AccountAppearance['density'] | null;
  elevation: AccountAppearance['elevation'] | null;
  fontFamily: AccountAppearance['fontFamily'] | null;
  fontSize: AccountAppearance['fontSize'] | null;
  lineHeight: AccountAppearance['lineHeight'] | null;
}

export interface AccountThemeState {
  schemaVersion: 2;
  base: ThemeReleaseReference | null;
  /** 账户私有背景图；与主题素材一样只能引用平台托管对象。 */
  background: ThemeAssetReference | null;
  preferences: UserVisualPreferences;
  updatedAt: string;
}

export interface ResolvedThemeRuntime {
  schemaVersion: 2;
  source: 'default' | 'marketplace';
  base: ThemeReleaseReference | null;
  manifest: ThemeManifestV2;
  preferences: UserVisualPreferences;
  background: ThemeAssetReference | null;
  /** 已鉴权且同源的素材地址，key 为 manifest.assets 的 id。 */
  assetUrls: Record<string, string>;
  /** 样式包中的 asset:// 引用已替换为同源签名 URL。 */
  styleSheet: string | null;
  /** 账户自定义背景图的同源签名地址；为空时使用主题场景素材。 */
  backgroundUrl: string | null;
  /** 供 Ant Design 和组件运行时使用的受控语义投影。 */
  appearance: AccountAppearance;
}

export interface ComponentThemeRuntime {
  schemaVersion: 2;
  appearance: AccountAppearance;
  surface: ThemeSurfaceRecipe;
  assetUrls: Record<string, string>;
}

const defaultPalette: ThemePalette = {
  primary: '#6857ED', canvas: '#F6F7FB', surface: '#FFFFFF', elevated: '#FFFFFF',
  text: '#172C4B', textMuted: '#647590', border: '#E5EAF3', success: '#16794C',
  warning: '#A96800', danger: '#C53D3D', info: '#146C94',
};

function recipe(
  tint: string,
  opacity: number,
  radius: number,
  input: Partial<Pick<ThemeSurfaceRecipe, 'blurPx' | 'saturation' | 'borderColor' | 'borderOpacity' | 'highlightColor' | 'highlightOpacity' | 'shadowColor' | 'shadow'>> = {},
): ThemeSurfaceRecipe {
  return {
    tint, opacity, radius,
    blurPx: input.blurPx ?? 0,
    saturation: input.saturation ?? 1.08,
    borderColor: input.borderColor ?? '#D8E0EF',
    borderOpacity: input.borderOpacity ?? 0.72,
    highlightColor: input.highlightColor ?? '#FFFFFF',
    highlightOpacity: input.highlightOpacity ?? 0.22,
    shadowColor: input.shadowColor ?? '#5E6D84',
    shadow: input.shadow ?? 'soft',
  };
}

/** 不依赖服务的默认主题，供登录投影和运行时共同使用。 */
export function createDefaultThemeManifest(): ThemeManifestV2 {
  const radius = DEFAULT_ACCOUNT_APPEARANCE.baseRadius;
  const lightSurfaces: Record<ThemeSurface, ThemeSurfaceRecipe> = {
    shell: recipe('#F6F7FB', 1, radius, { borderColor: '#E5EAF3', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    navigation: recipe('#FBFCFE', 1, radius, { borderColor: '#E5EAF3', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    header: recipe('#FFFFFF', 1, radius, { borderColor: '#E5EAF3', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    panel: recipe('#FFFFFF', 1, radius, { borderColor: '#E5EAF3', borderOpacity: 1, highlightOpacity: 0, shadowColor: '#64748B' }),
    card: recipe('#FFFFFF', 1, radius, { borderColor: '#E5EAF3', borderOpacity: 1, highlightOpacity: 0, shadowColor: '#718096' }),
    overlay: recipe('#FFFFFF', 1, radius, { borderColor: '#C7D3E4', borderOpacity: 1, highlightOpacity: 0.22, shadowColor: '#45556D', shadow: 'float' }),
    control: recipe('#FFFFFF', 1, radius, { borderColor: '#BFCDE0', borderOpacity: 0.92, highlightOpacity: 0.14, shadowColor: '#718096', shadow: 'none' }),
    component: recipe('#FFFFFF', 1, radius, { borderColor: '#E5EAF3', borderOpacity: 1, highlightOpacity: 0, shadowColor: '#64748B' }),
  };
  const darkSurfaces: Record<ThemeSurface, ThemeSurfaceRecipe> = {
    shell: recipe('#101722', 1, radius, { borderColor: '#293546', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    navigation: recipe('#121A26', 1, radius, { borderColor: '#293546', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    header: recipe('#182231', 1, radius, { borderColor: '#293546', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    panel: recipe('#182231', 1, radius, { borderColor: '#293546', borderOpacity: 1, highlightOpacity: 0, shadowColor: '#050A11' }),
    card: recipe('#182231', 1, radius, { borderColor: '#293546', borderOpacity: 1, highlightOpacity: 0, shadowColor: '#050A11' }),
    overlay: recipe('#202E3E', 1, radius, { borderColor: '#61728A', borderOpacity: 0.94, highlightColor: '#3A4D64', highlightOpacity: 0.2, shadowColor: '#03070C', shadow: 'float' }),
    control: recipe('#182231', 1, radius, { borderColor: '#425168', borderOpacity: 1, highlightOpacity: 0, shadow: 'none' }),
    component: recipe('#182231', 1, radius, { borderColor: '#293546', borderOpacity: 1, highlightOpacity: 0, shadowColor: '#050A11' }),
  };
  return {
    type: 'theme', schemaVersion: 2, name: '灵犀默认', description: '稳定、清晰的工作台基础主题',
    typography: { fontFamily: 'system', fontSize: 'medium', lineHeight: 'comfortable' }, density: 'comfortable', assets: [], styleSheet: null,
    light: { palette: defaultPalette, scene: { layers: [{ id: 'canvas', type: 'gradient', colors: ['#F6F7FB', '#F6F7FB', '#F6F7FB'], opacity: 1 }] }, surfaces: lightSurfaces, decorations: [] },
    dark: {
      palette: { ...defaultPalette, primary: '#8B82F1', canvas: '#101722', surface: '#182231', elevated: '#182231', text: '#EDF3FB', textMuted: '#B9C5D3', border: '#293546', success: '#4CB881', warning: '#E6AA48', danger: '#F08A8A', info: '#65C2E8' },
      scene: { layers: [{ id: 'canvas', type: 'gradient', colors: ['#101722', '#101722', '#101722'], opacity: 1 }] },
      surfaces: darkSurfaces, decorations: [],
    },
    allowPublicDerivatives: false,
  };
}

/** 用于 API 和 Marketplace 的纯校验，调用方自行决定如何呈现错误。 */
export function themeManifestError(input: ThemeManifestV2): string | null {
  const hex = /^#[0-9a-f]{6}$/i;
  const palette = (value: unknown): value is ThemePalette => Boolean(value) && Object.keys(value as object).length === 11 && Object.values(value as object).every((color) => typeof color === 'string' && hex.test(color));
  const identifier = (value: unknown) => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(value);
  if (!input || input.type !== 'theme' || input.schemaVersion !== 2 || !input.name?.trim() || input.name.length > 80 || !input.description?.trim() || input.description.length > 500) return '主题清单不完整';
  if (!['system', 'serif', 'mono'].includes(input.typography?.fontFamily) || !['small', 'medium', 'large'].includes(input.typography?.fontSize) || !['compact', 'comfortable', 'relaxed'].includes(input.typography?.lineHeight) || !['compact', 'comfortable'].includes(input.density)) return '主题排版配置无效';
  const assets = new Set<string>();
  for (const asset of input.assets ?? []) {
    if (!identifier(asset?.id) || assets.has(asset.id) || !THEME_ASSET_ROLES.includes(asset.role) || !asset.objectKey?.startsWith('themes/') || !['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/apng', 'application/json', 'video/mp4', 'video/webm', 'font/woff2'].includes(asset.contentType) || !/^[a-f0-9]{64}$/i.test(asset.sha256)) return '主题素材必须是平台托管的不可变资源';
    assets.add(asset.id);
  }
  const styleError = themeStyleSheetError(input.styleSheet, assets);
  if (styleError) return styleError;
  for (const variant of [input.light, input.dark].filter(Boolean) as ThemeVariant[]) {
    if (!palette(variant.palette) || !variant.scene?.layers?.length) return '主题场景或调色板无效';
    for (const layer of variant.scene.layers) {
      const usesAsset = ['image', 'texture', 'video'].includes(layer.type);
      const invalidAsset = usesAsset && (!layer.assetId || !assets.has(layer.assetId));
      const invalidColor = layer.type === 'color' && (!layer.color || !hex.test(layer.color));
      const invalidGradient = layer.type === 'gradient' && (!layer.colors || layer.colors.length < 2 || layer.colors.some((color) => typeof color !== 'string' || !hex.test(color)));
      if (!identifier(layer?.id) || !['color', 'gradient', 'image', 'texture', 'video'].includes(layer.type) || !Number.isFinite(layer.opacity) || layer.opacity < 0 || layer.opacity > 1 || invalidAsset || invalidColor || invalidGradient) return '主题场景层无效';
    }
    for (const surface of THEME_SURFACES) {
      const recipe = variant.surfaces?.[surface];
      if (!recipe || !hex.test(recipe.tint) || !hex.test(recipe.borderColor) || !hex.test(recipe.highlightColor) || !hex.test(recipe.shadowColor) || !Number.isInteger(recipe.radius) || recipe.radius < 0 || recipe.radius > 32 || !Number.isFinite(recipe.opacity) || recipe.opacity < 0 || recipe.opacity > 1 || !Number.isFinite(recipe.blurPx) || recipe.blurPx < 0 || recipe.blurPx > 48 || !Number.isFinite(recipe.saturation) || recipe.saturation < 0 || recipe.saturation > 2 || !Number.isFinite(recipe.borderOpacity) || recipe.borderOpacity < 0 || recipe.borderOpacity > 1 || !Number.isFinite(recipe.highlightOpacity) || recipe.highlightOpacity < 0 || recipe.highlightOpacity > 1 || !['none', 'soft', 'float'].includes(recipe.shadow)) return '主题表面配方无效';
    }
    for (const decoration of variant.decorations ?? []) {
      if (!identifier(decoration?.id) || !assets.has(decoration.assetId) || !['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'].includes(decoration.anchor) || !['small', 'medium', 'large'].includes(decoration.width) || !['none', 'float', 'drift', 'pulse'].includes(decoration.motion) || !Number.isFinite(decoration.opacity) || decoration.opacity < 0 || decoration.opacity > 1) return '主题装饰层无效';
    }
  }
  return null;
}

/**
 * 样式包是受限 CSS，不允许全局选择器、网络请求、布局属性或可执行内容。
 * 该校验同时运行在上传接口和发布清单校验，后者还会核对 asset:// 素材引用。
 */
export function themeStyleSheetError(input: ThemeStyleSheet | null | undefined, assetIds?: ReadonlySet<string>): string | null {
  if (input === null || input === undefined) return null;
  if (input.version !== 1 || typeof input.source !== 'string') return '主题样式包格式无效';
  const source = input.source.trim();
  if (!source || source.length > 48_000) return '主题样式包不能为空且不能超过 48KB';
  if (/<\/style|@(?:import|font-face|keyframes|media)|expression\s*\(|javascript:|behavior\s*:/i.test(source)) return '主题样式包包含不允许的规则';
  const blocks = [...source.replace(/\/\*[\s\S]*?\*\//g, '').matchAll(/([^{}]+)\{([^{}]*)\}/g)];
  if (!blocks.length || blocks.map((item) => item[0]).join('').replace(/\s/g, '').length !== source.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\s/g, '').length) return '主题样式包语法无效';
  const slots = new Set<string>(THEME_STYLE_SLOTS);
  const properties = new Set(['color', 'background', 'background-color', 'background-image', 'background-position', 'background-size', 'background-repeat', 'border', 'border-color', 'border-radius', 'box-shadow', 'backdrop-filter', 'filter', 'opacity', 'text-shadow', 'outline', 'outline-color', 'caret-color', 'scrollbar-color', 'transition']);
  for (const [, rawSelector = '', declarations = ''] of blocks) {
    const selectors = rawSelector.trim().split(',').map((value) => value.trim());
    if (!selectors.length || selectors.some((selector) => !slots.has(selector))) return '主题样式包只能使用平台提供的主题槽位';
    for (const declaration of declarations.split(';').map((value) => value.trim()).filter(Boolean)) {
      const separator = declaration.indexOf(':');
      if (separator < 1) return '主题样式包声明格式无效';
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if ((!properties.has(property) && !/^--(?:ws|aw)-[a-z0-9-]+$/.test(property)) || !value || value.length > 500 || /[{};]/.test(value)) return '主题样式包包含不允许的属性';
      for (const match of value.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi)) {
        const reference = (match[2] ?? '').trim();
        const assetId = reference.match(/^asset:\/\/([a-zA-Z0-9_-]{1,80})$/)?.[1];
        if (!assetId || (assetIds && !assetIds.has(assetId))) return '主题样式包只能引用本主题已上传的素材';
      }
      if (/url\(/i.test(value) && !/url\(\s*(['"]?)asset:\/\/[a-zA-Z0-9_-]{1,80}\1\s*\)/i.test(value)) return '主题样式包不允许加载外部资源';
    }
  }
  return null;
}

/** 将已验证样式包中的素材引用替换为当前账户可访问的同源直链。 */
export function resolveThemeStyleSheet(input: ThemeStyleSheet | null | undefined, assetUrls: Record<string, string>): string | null {
  if (!input?.source) return null;
  return input.source.replace(/asset:\/\/([a-zA-Z0-9_-]{1,80})/g, (_match, assetId: string) => assetUrls[assetId] ?? '');
}
